import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import ReactCrop from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import {
  Button,
  ButtonGroup,
  Card, Divider,
  HTMLTable,
  Icon,
  Popover,
  ResizeSensor,
  Switch,
  Text,
  Tooltip
} from '@blueprintjs/core'
import { produce } from 'immer'
import '@blueprintjs/core/lib/css/blueprint.css'
import '@blueprintjs/icons/lib/css/blueprint-icons.css'
import { GlobalContext, globalOnDemandVars } from '../../../helper/context'
import { undoManager } from '../../../helper/functions'
import { OverlayProps } from '../../../helper/configs'
import { MouseSensor } from 'libreact/lib/MouseSensor'
import { Mosaic } from 'react-mosaic-component'
import { thickScrollbar } from '../../../helper/CSSclasses'
import { defer } from 'lodash'
import { useTranslation } from 'react-i18next'
// -----------------------------------------------------------------------------------------------------------
// TODO 순환참조 안되게 하는거 먹히나?
import { CommonEditor, CommonRender } from '../../addon/addons/parts/forEachInsertForm/common'
import { Fragment, FragmentElement } from './fragment'

// const CommonEditor = () => (null)
// const CommonRender = () => (null)
// const Fragment = () => (null)
// const FragmentElement = () => (null)

const imagePopoverWidth = 300
const imagePopoverHeight = 300
function EditorForUploadType2 ({ id, window1Id, forType3 }) {
  const { t } = useTranslation()

  const item = useMemo(() => (globalOnDemandVars.uploadType2s()[id]), [id])

  const type = useMemo(() => (item.type), [item.type])

  const [content, setContent] = useState(item.content)
  const [switchForMultipleCard, setSwitchForMultipleCard] = useState(item.switchForMultipleCard)
  const [hideAll, setHideAll] = useState(item.hideAll)
  const [fragmentList, setFragmentList] = useState(item.fragmentList)

  const [fragmentGeneratingMode, setFragmentGeneratingMode] = useState(!forType3)

  // TODO 여기서 ref 가 제대로 전달 되나?
  const editorRef = useRef(null)
  const MediaElement = useMemo(() => (fragmentGeneratingMode ? <CommonRender type={type} contentData={content} /> : null), [content, fragmentGeneratingMode, type])

  // -----------------------------------------------------------------------------------------------------------

  const triggerGlobalChange = useCallback(
    () => {
      globalOnDemandVars.set_changedUploadType2Info({ id, window1Id })
      globalOnDemandVars.set_uploadType2ChangeDetect(!globalOnDemandVars.uploadType2ChangeDetect())
    },
    [id, window1Id]
  )

  const context = useContext(GlobalContext)
  useEffect(() => {
    const changedUploadType2Info = globalOnDemandVars.changedUploadType2Info()
    if (changedUploadType2Info.id === id && changedUploadType2Info.window1Id !== window1Id) {
      if (process.env.NODE_ENV !== 'production') {
        console.tron.log('uploadType2 / create 0')
      }
      const globalUploadType2 = globalOnDemandVars.uploadType2s()[id]
      setFragmentList(globalUploadType2.fragmentList)
      setSwitchForMultipleCard(globalUploadType2.switchForMultipleCard)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.uploadType2ChangeDetect])

  const prevId = useRef(id)
  useEffect(() => {
    if (id !== prevId.current) {
      if (process.env.NODE_ENV !== 'production') {
        console.tron.log('uploadType2 / create 0.5')
      }
      setFragmentList(item.fragmentList)
      setSwitchForMultipleCard(item.switchForMultipleCard)
      prevId.current = id
    }
  }, [item, id])

  // -----------------------------------------------------------------------------------------------------------

  const createFragmentWithGlobal = useCallback(
    (newFragment, config = {}) => {
      setFragmentList(produce(fragmentList, draft => {
        draft.unshift(newFragment)
      }))
      item.createFragment(newFragment)
      triggerGlobalChange()
      // -----------------------------------------------------------------------------------------------------------
      if (!config.notUndoRedo) {
        undoManager.add({
          redo: () => {
            createFragmentWithGlobal(newFragment, { notUndoRedo: true })
            defer(() => { showChange() })
          },
          undo: () => {
            // deleteFragmentWithGlobal(newFragment.id, { notUndoRedo: true })
            const fragmentId = newFragment.id
            // const index = fragmentList.findIndex((fragment) => (fragment.id === fragmentId))
            setFragmentList(fragmentList)
            item.deleteFragment(fragmentId)
            triggerGlobalChange()
            defer(() => { showChange() })
          }
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fragmentList, item, triggerGlobalChange]
  )
  const updateFragmentWithGlobal = useCallback(
    (fragmentId, update) => {
      fragmentList.find((fragment) => (fragment.id === fragmentId)).update(update)
      item.updateFragment(fragmentId, update)
      triggerGlobalChange()
    },
    [fragmentList, item, triggerGlobalChange]
  )
  const deleteFragmentWithGlobal = useCallback(
    (fragmentId, config = {}) => {
      let theFragment
      const index = fragmentList.findIndex((fragment) => {
        if (fragment.id === fragmentId) {
          theFragment = fragment
          return true
        }
      })
      setFragmentList(produce(fragmentList, draft => {
        draft.splice(index, 1)
      }))
      item.deleteFragment(fragmentId)
      triggerGlobalChange()
      // -----------------------------------------------------------------------------------------------------------
      if (!config.notUndoRedo) {
        undoManager.add({
          redo: () => {
            deleteFragmentWithGlobal(fragmentId, { notUndoRedo: true })
            defer(() => { showChange() })
          },
          undo: () => {
            setFragmentList(fragmentList)
            item.createFragment(theFragment)
            triggerGlobalChange()
            defer(() => { showChange() })
          }
        })
      }
    },
    [fragmentList, item, triggerGlobalChange]
  )

  // -----------------------------------------------------------------------------------------------------------

  const [cropChange, setCropChange] = useState(null)
  // const [currentCrop, setCurrentCrop] = useState(null)

  const showChange = useCallback(
    () => {
      const currentCrop = cropChange
      setCropChange({})
      setCropChange(currentCrop)
    },
    [cropChange]
  )

  // -----------------------------------------------------------------------------------------------------------

  const reactCrop = useMemo(() => (
    <ReactCrop crop={cropChange} onChange={(crop, percentCrop) => {
      setCropChange(percentCrop)
    }} onComplete={(crop, percentCrop) => {
      if (percentCrop.width > 0.1 && percentCrop.height > 0.1) {
        // new ImageClass(content.name, content.publicId, content.width, content.height, {
        //   x: percentCrop.x / 100,
        //   y: percentCrop.y / 100,
        //   width: percentCrop.width / 100,
        //   height: percentCrop.height / 100
        // })
        createFragmentWithGlobal(new Fragment({
          uploadType2Id: id,
          cropConfig: {
            x: percentCrop.x / 100,
            y: percentCrop.y / 100,
            width: percentCrop.width / 100,
            height: percentCrop.height / 100
          }
        }))
        showChange()
      }
    }} renderComponent={
      <div>
        <div css={css`pointer-events: none;`}>{MediaElement}</div>
      </div>
    } />
  ), [MediaElement, createFragmentWithGlobal, cropChange, id, showChange])

  const [imageResponsiveSize, setImageResponsiveSize] = useState({
    width: 0,
    height: 0
  })

  const withMouseSensor = useMemo(() => {
    const croppedImageWidth = content.width * (content.cropConfig ? content.cropConfig.width : 1)
    const croppedImageHeight = content.height * (content.cropConfig ? content.cropConfig.height : 1)

    return (
      !content.isImage ? null : <MouseSensor bond>{(state) => (
        <Popover disabled={croppedImageHeight - imageResponsiveSize.height < 50} {...OverlayProps} interactionKind={'hover'} content={
          <div
            css={css`position: relative; overflow: hidden; width: 100%; height: 100%; min-width: ${imagePopoverWidth}px; min-height: ${imagePopoverHeight}px;`}>
            <Icon css={css`z-index: 10; position: absolute; top: calc(50% - 8px); left: calc(50% - 8px);`} icon={'plus'} />
            <div style={{
              top: (((-1 * state.elY) * Math.floor(croppedImageHeight) / imageResponsiveSize.height) + (imagePopoverHeight / 2) + 'px'),
              left: (((-1 * state.elX) * Math.floor(croppedImageWidth) / imageResponsiveSize.width) + (imagePopoverWidth / 2) + 'px')
            }} css={css`
  width: ${Math.floor(croppedImageWidth)}px; height: ${Math.floor(croppedImageHeight)}px;
  position: absolute;
  `}>
              {MediaElement}
            </div>
          </div>
        }>
          <ResizeSensor onResize={(entries) => {
            const width = entries[0].contentRect.width
            const height = entries[0].contentRect.height
            setImageResponsiveSize({ width, height })
          }}>
            <div {...state.bond}>{reactCrop}</div>
          </ResizeSensor>
        </Popover>
      )}</MouseSensor>
    )
  }, [MediaElement, content.cropConfig, content.height, content.isImage, content.width, imageResponsiveSize.height, imageResponsiveSize.width, reactCrop])

  const MediaSection = useMemo(() => (
    <div css={css`height: 100%; display: flex;`}>
      {forType3 ? (
        <Card className={'fullFill'}
          css={css`border-top-right-radius: 0 !important; border-bottom-right-radius: 0 !important; box-shadow: none; border: solid var(--gray4); border-width: 1px 0 1px 1px;`}>
          <Tooltip {...OverlayProps} position={'left'} content={t('Check as Questions')}>
            <Button icon={'font'} large active={fragmentGeneratingMode} onClick={async () => {
            // TODO 여기서 save 후에 제대로 반영이 되나?
              const reverseBoolean = !fragmentGeneratingMode
              if (reverseBoolean) {
                await editorRef.current.onSave()
                setOpenFragmentSection(true)
              }
              setFragmentGeneratingMode(reverseBoolean)
            }} />
          </Tooltip>
        </Card>) : null}

      <Card css={css`overflow: auto; max-width: 100%; width: 100%; max-height: 100%; border-top-left-radius: 0 !important; border-bottom-left-radius: 0 !important; box-shadow: none; border: 1px solid var(--gray4);`}>
        <div css={css`display: inline-block; width: 100%; overflow: auto;`}>
          <div css={css`border: 1px var(--gray14) solid; display: inline-block; min-width: 100%;`}>
            {fragmentGeneratingMode ? (
              content.isImage ? withMouseSensor : reactCrop
            ) : (
              <CommonEditor editorRef={editorRef} type={type} item={globalOnDemandVars.uploadType2s()[id]} setContent={setContent} />
            )}
          </div>
        </div>
      </Card>
    </div>
  ), [content.isImage, forType3, fragmentGeneratingMode, id, reactCrop, t, type, withMouseSensor])
  const FragmentSection = useMemo(() => (
    <div css={css`padding: var(--s1); background-color: var(--sidebarColor); border-top-left-radius: 1em; border-bottom-left-radius: 1em; position: relative;`}>
      <Button icon={<Icon icon={'chevron-right'} iconSize={24} color={'#4b4b4b'} />} minimal css={css`position: absolute; top: var(--s-2); left: 0;`} onClick={() => { setOpenFragmentSection(false) }} />
      <div css={css`display: flex; flex-direction: column; padding: 0; height: 100%; border-top-left-radius: 1em; border-bottom-left-radius: 1em; background-color: var(--baseColor);`}>
        <div css={css`flex: auto; overflow: auto;`}>
          <HTMLTable>
            <tr css={css`display: flex; flex-wrap: wrap; justify-content: flex-start; align-items: flex-end;`}>
              {fragmentList.map((fragment) => (
                <td key={fragment.id}>
                  <FragmentElement type={type} fragment={fragment}
                    updateFragment={updateFragmentWithGlobal} deleteFragment={deleteFragmentWithGlobal}
                    showChange={showChange} />
                </td>
              ))}
            </tr>
          </HTMLTable></div>

        <div css={css`padding-top: var(--s-1); border-top: 1px solid var(--gray4);`}>
          <div
            css={css`width: fit-content; margin: auto; display: flex; gap: var(--s1);`}>
            <Tooltip
              content={<div>
                {t('In the Memorize section, it covers all the parts you have cropped')}
                <br />
                {t('Otherwise, it covers only the parts you need to recall.')}
              </div>} {...OverlayProps}
              targetTagName={'div'}>
              <div css={css`display: flex;`}>
                <Switch inline checked={hideAll} onChange={(e) => {
                  const checked = e.target.checked
                  setHideAll(checked)
                  item.setHideAll(checked)
                  triggerGlobalChange()
                  // -----------------------------------------------------------------------------------------------------------
                  undoManager.add({
                    redo: () => {
                      setHideAll(checked)
                      item.setHideAll(checked)
                      triggerGlobalChange()
                    },
                    undo: () => {
                      setHideAll(!checked)
                      item.setHideAll(!checked)
                      triggerGlobalChange()
                    }
                  })
                }} />
                <Text ellipsize>{t('Hide All Fragments')}</Text>
              </div>
            </Tooltip>
            <Tooltip
              content={t('It shows multiple fragments in the Memorize section')} {...OverlayProps}
              targetTagName={'div'}>
              <div css={css`display: flex;`}>
                <Switch inline checked={switchForMultipleCard} onChange={(e) => {
                  const checked = e.target.checked
                  setSwitchForMultipleCard(checked)
                  item.setSwitchForMultipleCard(checked)
                  triggerGlobalChange()
                  // -----------------------------------------------------------------------------------------------------------
                  undoManager.add({
                    redo: () => {
                      setSwitchForMultipleCard(checked)
                      item.setSwitchForMultipleCard(checked)
                      triggerGlobalChange()
                    },
                    undo: () => {
                      setSwitchForMultipleCard(!checked)
                      item.setSwitchForMultipleCard(!checked)
                      triggerGlobalChange()
                    }
                  })
                }} />
                <Text ellipsize>{t('Show Multiple Fragments')}</Text>
              </div>
            </Tooltip>
          </div>
        </div>
      </div></div>
  ), [deleteFragmentWithGlobal, fragmentList, item, hideAll, showChange, switchForMultipleCard, t, triggerGlobalChange, type, updateFragmentWithGlobal])

  const [mosaicValue, setMosaicValue] = useState({
    direction: 'row',
    first: 'MediaSection',
    second: 'FragmentSection',
    splitPercentage: 50
  })

  const [openFragmentSection, setOpenFragmentSection] = useState(false)

  return useMemo(() => (
    <div css={css`position: absolute; width: 100%; height: 100%;`}>
      {!openFragmentSection ? (
        <div css={css`display: flex; height: 100%;`}>
          <div css={css`flex-grow: 1; margin: 3px;`}>
            {MediaSection}
          </div>
          <div css={css`width: var(--s2); background-color: var(--sidebarColor); border-top-left-radius: 1em; border-bottom-left-radius: 1em; position: relative;`}>
            <Button icon={<Icon icon={'chevron-left'} iconSize={24} color={'#4b4b4b'} />} minimal css={css`position: absolute; top: var(--s-2);`} onClick={() => { setOpenFragmentSection(true) }} />
          </div>
        </div>
      ) : (
        <div css={css`position: absolute; width: 100%; height: 100%;
        & .mosaic-tile {
          margin: 0 !important;
        }`}>
          <Mosaic
            renderTile={(id) => {
              switch (id) {
                case 'MediaSection':
                  return MediaSection
                case 'FragmentSection':
                  return FragmentSection
              }
            }}
            value={mosaicValue}
            onChange={setMosaicValue}
          />
        </div>
      )}
    </div>
  ), [FragmentSection, MediaSection, mosaicValue, openFragmentSection])
}

export default EditorForUploadType2
